import { AVAILABILITY_WS_EVENTS } from './constants';
import { emitAvailabilitySocketEvent } from './events';
import {
  AvailabilitySocketEnvelope,
  AvailabilitySocketInboundMessageType,
  AvailabilitySocketMessage,
  AvailabilitySocketMessageType,
} from './types';

const getErrorMessage = (reason: unknown) => (reason instanceof Error ? reason.message : String(reason));

type AvailabilitySocketEventName = (typeof AVAILABILITY_WS_EVENTS)[keyof typeof AVAILABILITY_WS_EVENTS];

const inboundMessageEventByType: Record<AvailabilitySocketInboundMessageType, AvailabilitySocketEventName> = {
  [AvailabilitySocketMessageType.InitialState]: AVAILABILITY_WS_EVENTS.INITIAL_STATE,
  [AvailabilitySocketMessageType.ActivityUpdate]: AVAILABILITY_WS_EVENTS.ACTIVITY_UPDATE,
  [AvailabilitySocketMessageType.InterruptionDelivered]: AVAILABILITY_WS_EVENTS.INTERRUPTION_DELIVERED,
  [AvailabilitySocketMessageType.InterruptionCancelled]: AVAILABILITY_WS_EVENTS.INTERRUPTION_CANCELLED,
  [AvailabilitySocketMessageType.InterruptionResponseStatusChanged]:
    AVAILABILITY_WS_EVENTS.INTERRUPTION_RESPONSE_STATUS_CHANGED,
  [AvailabilitySocketMessageType.InitialInterruptions]: AVAILABILITY_WS_EVENTS.INITIAL_INTERRUPTION,
  [AvailabilitySocketMessageType.FocusSchedulesUpdated]: AVAILABILITY_WS_EVENTS.FOCUS_SCHEDULES_UPDATED,
  [AvailabilitySocketMessageType.NotificationDelivered]: AVAILABILITY_WS_EVENTS.NOTIFICATION_DELIVERED,
};

const knownInboundMessageTypes = new Set<string>(Object.keys(inboundMessageEventByType));

export const parseAvailabilitySocketMessage = (rawMessage: string): AvailabilitySocketMessage => {
  try {
    const envelope = JSON.parse(rawMessage) as AvailabilitySocketEnvelope;
    const meta = {
      request_id: envelope.request_id,
      timestamp: envelope.timestamp,
    };

    if (knownInboundMessageTypes.has(envelope.type)) {
      return {
        type: envelope.type as AvailabilitySocketInboundMessageType,
        meta,
        payload: envelope.payload,
      };
    }

    return {
      type: AvailabilitySocketMessageType.Unknown,
      meta,
      messageType: envelope.type,
      payload: envelope.payload,
    };
  } catch (reason) {
    return {
      type: AvailabilitySocketMessageType.ParseError,
      raw: rawMessage,
      error: getErrorMessage(reason),
    };
  }
};

const emitAvailabilitySocketMessageEvents = (message: AvailabilitySocketMessage) => {
  emitAvailabilitySocketEvent(AVAILABILITY_WS_EVENTS.MESSAGE, message);

  if (
    message.type === AvailabilitySocketMessageType.Unknown ||
    message.type === AvailabilitySocketMessageType.ParseError
  ) {
    return;
  }

  const eventName = inboundMessageEventByType[message.type];

  if (eventName) {
    emitAvailabilitySocketEvent(eventName, message.payload);
  }
};

export const dispatchAvailabilitySocketMessage = (rawMessage: string) => {
  const message = parseAvailabilitySocketMessage(rawMessage);

  emitAvailabilitySocketMessageEvents(message);

  return message;
};
